<template>
  <div class="order-book">
    <!-- Header -->
    <div class="order-book__header">
      <div class="order-book__tabs">
        <button 
          v-for="tab in options" 
          :key="tab"
          class="order-book__tab"
          :class="{ 'order-book__tab--active': option === tab }"
          @click="emitOption(tab)"
        >
          <span class="order-book__tab-icon" v-if="tab === 'Buy Orders'">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 19V5M5 12L12 5L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="order-book__tab-icon" v-else-if="tab === 'Sell Orders'">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19M5 12L12 19L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          {{ tab }}
        </button>
      </div>
      <div class="order-book__actions">
        <button class="order-book__filter-btn" @click="showFilter = !showFilter">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Filter</span>
        </button>
        <FilterComponent
          v-show="showFilter"
          class="order-book__filter-dropdown"
          title="Filter Orders"
          :options="filterOptions"
          @close="showFilter = !showFilter"
          @filter="filter"
        />
      </div>
    </div>

    <!-- Table -->
    <div class="order-book__table-wrapper">
      <table v-if="orders.length > 0" class="order-book__table">
        <thead>
          <tr>
            <th>Token</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Price</th>
            <th>Total Value</th>
            <th>Status</th>
            <th>Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in orders" :key="order._id" class="order-book__row">
            <td>
              <div class="order-book__token">
                <img :src="order.token?.image" :alt="order.token?.symbol" class="order-book__token-img" />
                <span class="order-book__token-symbol">{{ order.token?.symbol }}</span>
              </div>
            </td>
            <td>
              <span class="order-book__type" :class="'order-book__type--' + (order.type || 'buy').toLowerCase()">
                <svg v-if="(order.type || 'Buy') === 'Buy'" width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M12 19V5M5 12L12 5L19 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12L12 19L19 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ order.type || 'Buy' }}
              </span>
            </td>
            <td>
              <span class="order-book__amount">{{ order.amount?.toFixed(2) }}</span>
            </td>
            <td>
              <span class="order-book__price">{{ formatPrice(order.price) }}</span>
            </td>
            <td>
              <span class="order-book__total">{{ formatPrice(order.amount * order.price) }}</span>
            </td>
            <td>
              <span class="order-book__status" :class="'order-book__status--' + getStatusClass(order.status)">
                {{ getStatusLabel(order.status) }}
              </span>
            </td>
            <td>
              <div class="order-book__date">
                <span class="order-book__date-day">{{ formatDate(order.createdAt) }}</span>
                <span class="order-book__date-time">{{ formatTime(order.createdAt) }}</span>
              </div>
            </td>
            <td>
              <button
                v-if="order.status === 'Active'"
                class="order-book__cancel-btn"
                @click="cancel(order._id)"
              >
                Cancel
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-else-if="!smallLoading" class="order-book__empty">
        <div class="order-book__empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
            <path d="M4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4 19.5V19.5C4 18.1193 5.11929 17 6.5 17H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 10H15M9 14H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="order-book__empty-title">No orders yet</h3>
        <p class="order-book__empty-text">Orders will appear here once trading activity begins.</p>
      </div>

      <!-- Loading -->
      <div v-if="smallLoading && table === 'dealOrderTable'" class="order-book__loading">
        <SmallLoader />
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="this.$route.name !== 'Overview' && orders.length > 0" class="order-book__pagination">
      <Pagination
        :totalPage="pages"
        :currentPage="page"
        :maxVisible="5"
        @pageChange="gotoPage"
      />
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState, mapActions } from "vuex";
  import Pagination from "@/components/navigation/Pagination";
  import FilterComponent from "@/components/general/FilterComp";
  import SmallLoader from "@/components/notification/SmallLoader";
  import {
    formatISOTime,
    formatISODate,
    formatAmountToDollar,
  } from "@/utils/helpers.js";

  export default {
    name: "DealOrderBookTable",

    components: { Pagination, FilterComponent, SmallLoader },

    data() {
      return {
        showFilter: false,
        options: ["All Orders", "Buy Orders", "Sell Orders"],
        option: "All Orders",
        filterOptions: [
          {
            name: "Completed",
            value: "Fulfilled",
          },
          {
            name: "Pending",
            value: "Active",
          },
          {
            name: "Failed",
            value: "Terminated",
          },
        ],
        currentOption: "",
        orders: [],
        table: "",
      };
    },

    computed: {
      ...mapState({
        smallLoading: (state) => state.smallLoading,
        orderBook: (state) => state.assetModule.orderBook,
        page: (state) => state.assetModule.orderPage,
        pages: (state) => state.assetModule.orderPages,
      }),
      ...mapGetters("TokenModule", ["token"]),
    },

    methods: {
      ...mapActions("assetModule", ["getTokenOrders", "cancelOrder"]),

      formatPrice(amount) {
        return formatAmountToDollar(amount, 2, "USD");
      },

      formatDate(date) {
        return formatISODate(date);
      },

      formatTime(date) {
        return formatISOTime(date);
      },

      getStatusLabel(status) {
        const statusMap = {
          Active: "Pending",
          Fulfilled: "Completed",
          Terminated: "Cancelled",
        };
        return statusMap[status] || status;
      },

      getStatusClass(status) {
        const classMap = {
          Active: "pending",
          Fulfilled: "completed",
          Terminated: "cancelled",
        };
        return classMap[status] || "pending";
      },

      filter(status) {
        let payload = null;
        const id = this.token.token?._id;
        if (status) {
          payload = {
            id,
            page: 1,
            type: this.currentOption,
            limit: 10,
            status,
          };
        } else {
          payload = {
            id,
            page: 1,
            type: this.currentOption,
            limit: 10,
          };
        }
        this.orders = [];
        this.table = "dealOrderTable";
        this.getTokenOrders(payload);
      },

      emitOption(optionValue) {
        const id = this.token.token?._id;
        this.table = "dealOrderTable";
        this.orders = [];
        let type;

        switch (optionValue) {
          case "All Orders":
            type = null;
            this.currentOption = null;
            this.option = "All Orders";
            break;
          case "Buy Orders":
            type = "Buy";
            this.currentOption = "Buy";
            this.option = "Buy Orders";
            break;
          case "Sell Orders":
            type = "Sell";
            this.currentOption = "Sell";
            this.option = "Sell Orders";
            break;

          default:
            break;
        }
        const payload = {
          id,
          page: 1,
          type,
          limit: 10,
        };
        this.getTokenOrders(payload);
      },

      gotoPage(page) {
        const id = this.token.token?._id;
        this.orders = [];
        this.table = "dealOrderTable";
        const payload = {
          id,
          page: page,
          type: this.currentOption,
          limit: 10,
        };
        this.getTokenOrders(payload);
      },

      updateOrders(orderData) {
        this.table = "";
        this.orders = orderData;
      },

      async cancel(orderId) {
        this.orders = [];
        this.table = "dealOrderTable";
        const response = await this.cancelOrder(orderId);
        if (response) {
          let limit;
          this.$route.name === "Invest" ? (limit = 5) : (limit = 10);
          const payload = {
            page: this.page,
            type: this.currentOption,
            limit: limit,
          };
          this.getTokenOrders(payload);
        }
      },
    },

    watch: {
      orderBook(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.updateOrders(this.orderBook);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .order-book {
    background: #ffffff;
    border-radius: 12px;
    padding: 24px;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
      margin-bottom: 24px;
    }

    &__tabs {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    &__tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 16px;
      font-size: 14px;
      font-weight: 500;
      color: #64748b;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f1f5f9;
        border-color: #cbd5e1;
      }

      &--active {
        background: #4D84FF;
        color: #ffffff;
        border-color: #4D84FF;

        &:hover {
          background: #3b6fe0;
          border-color: #3b6fe0;
        }
      }
    }

    &__tab-icon {
      display: flex;
      align-items: center;
    }

    &__actions {
      position: relative;
    }

    &__filter-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      font-size: 14px;
      font-weight: 500;
      color: #475569;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
      }
    }

    &__filter-dropdown {
      position: absolute;
      right: 0;
      top: 100%;
      margin-top: 8px;
      z-index: 10;
    }

    &__table-wrapper {
      overflow-x: auto;
    }

    &__table {
      width: 100%;
      border-collapse: collapse;

      th {
        padding: 12px 16px;
        text-align: left;
        font-size: 12px;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;

        &:first-child {
          border-radius: 8px 0 0 0;
        }

        &:last-child {
          border-radius: 0 8px 0 0;
        }
      }

      td {
        padding: 16px;
        font-size: 14px;
        color: #1e293b;
        border-bottom: 1px solid #f1f5f9;
        vertical-align: middle;
      }
    }

    &__row {
      transition: background 0.15s ease;

      &:hover {
        background: #f8fafc;
      }

      &:last-child td {
        border-bottom: none;
      }
    }

    &__token {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &__token-img {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      object-fit: cover;
      background: #f1f5f9;
    }

    &__token-symbol {
      font-weight: 600;
      color: #0f172a;
    }

    &__type {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      font-size: 12px;
      font-weight: 600;
      border-radius: 6px;

      &--buy {
        color: #059669;
        background: #ecfdf5;
      }

      &--sell {
        color: #dc2626;
        background: #fef2f2;
      }
    }

    &__amount {
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    &__price,
    &__total {
      font-variant-numeric: tabular-nums;
    }

    &__total {
      font-weight: 600;
      color: #0f172a;
    }

    &__status {
      display: inline-flex;
      padding: 6px 12px;
      font-size: 12px;
      font-weight: 600;
      border-radius: 20px;

      &--pending {
        color: #d97706;
        background: #fffbeb;
      }

      &--completed {
        color: #059669;
        background: #ecfdf5;
      }

      &--cancelled {
        color: #dc2626;
        background: #fef2f2;
      }
    }

    &__date {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    &__date-day {
      font-weight: 500;
      color: #1e293b;
    }

    &__date-time {
      font-size: 12px;
      color: #94a3b8;
    }

    &__cancel-btn {
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 500;
      color: #dc2626;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #fee2e2;
        border-color: #fca5a5;
      }
    }

    &__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 64px 24px;
      text-align: center;
    }

    &__empty-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 80px;
      height: 80px;
      margin-bottom: 16px;
      color: #94a3b8;
      background: #f8fafc;
      border-radius: 50%;
    }

    &__empty-title {
      margin: 0 0 8px;
      font-size: 18px;
      font-weight: 600;
      color: #1e293b;
    }

    &__empty-text {
      margin: 0;
      font-size: 14px;
      color: #64748b;
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 48px;
    }

    &__pagination {
      display: flex;
      justify-content: flex-end;
      padding-top: 20px;
      border-top: 1px solid #f1f5f9;
      margin-top: 16px;
    }
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .order-book {
      padding: 16px;

      &__header {
        flex-direction: column;
        align-items: flex-start;
      }

      &__tabs {
        width: 100%;
        overflow-x: auto;
        padding-bottom: 8px;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      &__tab {
        white-space: nowrap;
        flex-shrink: 0;
      }

      &__table {
        th, td {
          padding: 12px 8px;
          font-size: 13px;
        }
      }

      &__token-img {
        width: 28px;
        height: 28px;
      }

      &__date {
        min-width: 80px;
      }
    }
  }
</style>
